/**
 * While the pointer is over a map, the wheel must zoom the map — not scroll
 * the page out from under it. Mapbox already zooms toward the cursor when
 * `scrollZoom` is on; this lock only stops the ancestor ScrollView from
 * taking the same event.
 *
 * React's delegated `onWheel` is passive, so preventDefault there is a no-op.
 * A native non-passive listener on the map host is what actually holds the
 * page still. The listener bubbles: Mapbox on the canvas sees the event
 * first and zooms, then we cancel the page scroll.
 */
import { useEffect, type RefObject } from 'react'
import { Platform } from 'react-native'

export function useLockPageScrollOverMap(ref: RefObject<unknown>): void {
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const node = resolveDomNode(ref.current)
    if (!node) return

    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      event.stopPropagation()
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  })
}

function resolveDomNode(value: unknown): HTMLElement | null {
  if (!value || typeof value !== 'object') return null
  if (typeof (value as HTMLElement).addEventListener === 'function') {
    return value as HTMLElement
  }
  const record = value as { _nativeNode?: unknown; node?: unknown }
  const host = record._nativeNode ?? record.node
  if (host && typeof (host as HTMLElement).addEventListener === 'function') {
    return host as HTMLElement
  }
  return null
}
